import { fetchEventSource } from "@microsoft/fetch-event-source";
import { useEffect, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import { useCacheBuster } from "react-cache-buster";
import { useDispatch, useSelector } from "react-redux";
import SendArrow from "../../Images/SendArrow";
import StopIcon from "../../Images/StopIcon";
import { selectBearer, setExpired } from "../../slice/bearerSlice";
import {
	setActiveConversationUuid,
	setIsTopicClickable,
} from "../../slice/ConversationSlice";
import {
	selectInputMessage,
	selectLineBreak,
	selectSend,
	selectUrlSource,
	selectUrlText,
	setInputMessage,
	setLineBreak,
	setSend,
	setUrlSource,
	setUrlText,
} from "../../slice/InputMessageSlice";
import {
	selectShowLoader,
	selectStreaming,
	selectTranslating,
	selectWaitingForMaxQueryResponse,
	setShowLoader,
	setStreaming,
	setWaitingForMaxQueryResponse,
} from "../../slice/loaderSlice";
import {
	deleteMessage,
	selectMessages,
	setAllTopicMessagesAsObsolete,
	setMessage,
	updateMessage,
	updateMessageText,
} from "../../slice/MessageSlice";
import {
	setShowLogin,
	setShowMaximumQueryLimit,
} from "../../slice/ModalVisibilitySlice";
import { setRefreshProfileHistory } from "../../slice/profileHistorySlice";
import {
	selectCounterInitTime,
	selectCounterResetTime,
	selectQueryDoneCounter,
	setCounterPlusOne,
} from "../../slice/unloggedUserSlice";
import { selectRequest, setRequest } from "../../slice/userActionsSlice";
import { selectUserData } from "../../slice/userDataSlice";
import {
	openVariantValidator,
	resetVariantValidator,
	selectSelectedVariant,
	selectVariantSelectionContext,
	selectVariantValidatorFeedback,
	selectVariantValidatorShow,
	selectVariantValidatorStatusCode,
} from "../../slice/VariantValidatorSlice";
import { sendMessageGA } from "../../GoogleAnalytics4/GoogleAnalyticsEvents";
import citiesCountryMapping from "../../constants/citiesCountryMapping";
import { Url } from "../../constants/global";
import {
	getRequestController,
	resetRequestController,
} from "../../utils/requestController";
import { validateVariant } from "../../utils/validateFunction";
import {
	createSelectedVariantSearchMessage,
	createSelectedVariant,
	createSelectedVariantLabel
} from "../../utils/variantSelection";

function SendMessageButton() {
	const inputMessage = useSelector(selectInputMessage);
	const messages = useSelector(selectMessages);
	const send = useSelector(selectSend);
	const buttonRef = useRef(null);
	const dispatch = useDispatch();
	const streaming = useSelector(selectStreaming);
	const translating = useSelector(selectTranslating);
	const loader = useSelector(selectShowLoader);
	const [disable, setDisable] = useState(false);
	const reload = useRef(true);
	const text = useSelector(selectUrlText);
	const linebreak = useSelector(selectLineBreak);
	const urlParams = new URLSearchParams(window.location.search);
	const { checkCacheStatus } = useCacheBuster();
	const user = useSelector(selectUserData).user;
	const [stopSearch, setStopSearch] = useState(false);
	const nRequest = useSelector(selectRequest);
	const logToken = useSelector(selectBearer);
	const unloggedUserQueryDone = useSelector(selectQueryDoneCounter);
	const unloggedUserCounterInitTime = useSelector(selectCounterInitTime);
	const NOT_LOGGED_USER_MAX_ALLOWED_QUERIES = 5;
	const NOT_LOGGED_USER_COUNTER_RESET_TIME = useSelector(
		selectCounterResetTime,
	);
	const waitingForMaxQueryResponse = useSelector(
		selectWaitingForMaxQueryResponse,
	);
	const [triggerAfterUpdatingBearer, setTriggerAfterUpdatingBearer] =
		useState(false);
	const urlSource = useSelector(selectUrlSource);
	const variantValidatorShow = useSelector(selectVariantValidatorShow);
	const variantSelectionContext = useSelector(selectVariantSelectionContext);
	const variantValidatorFeedback = useSelector(selectVariantValidatorFeedback);
	const variantValidatorStatusCode = useSelector(
		selectVariantValidatorStatusCode,
	);

	useEffect(() => {
		if (
			window.location.pathname === "/search" &&
			urlParams.get("text") !== null &&
			text !== urlParams.get("text")
		) {
			console.log(
				"--> search",
				window.location.search,
				window.location.pathname,
				urlParams,
				urlParams.get("text"),
			);
			dispatch(setUrlText(urlParams.get("text")));
			dispatch(setInputMessage(urlParams.get("text")));
			dispatch(setSend(!send));
			dispatch(setLineBreak(true));
			dispatch(setUrlSource(getExternalLinkSource()));
		} else if (!reload.current || send === "send") {
			console.log("send", reload.current, send, send === "send");
			if (!["ucsc", "md"].includes(urlSource)) dispatch(setUrlSource("")); // se da ucsc mantengo l'ambiente d'origine per il check sull'unlogged modal
			buttonRef.current.click();
		} else {
			dispatch(setUrlSource(""));
		}
		reload.current = false;
	}, [send]);

	useEffect(() => {
		if (streaming) {
			const onBeforeUnload = (e) => {
				if (streaming) {
					//e.preventDefault(); dialog
					resetRequestController();
					if (messages[messages.length - 1].type === "topic") {
						dispatch(
							updateMessage({
								i: messages.length - 2,
								obj: {
									stopText:
										"\nVariant search has been interrupted by the user.",
								},
							}),
						);
						dispatch(deleteMessage());
					} else {
						if (loader) {
							dispatch(
								updateMessage({
									i: messages.length - 1,
									obj: {
										stopText:
											"Variant search has been interrupted by the user.",
										text: "",
									},
								}),
							);
						} else {
							dispatch(
								updateMessage({
									i: messages.length - 1,
									obj: {
										stopText:
											"\nVariant search has been interrupted by the user.",
									},
								}),
							);
						}
					}
				}
				//e.returnValue = streaming;
				//return streaming;
			};

			window.addEventListener("beforeunload", onBeforeUnload);

			return (res) => {
				window.removeEventListener("beforeunload", onBeforeUnload);
			};
		}
	}, [streaming]);

	useEffect(() => {
		if (translating) {
			setDisable(true);
		} else {
			setDisable(false);
		}
	}, [translating]);

	useEffect(() => {
		if (stopSearch) {
			setStopSearch(false);
			dispatch(setStreaming(false));
		}
	}, [stopSearch]);

	useEffect(() => {
		if (triggerAfterUpdatingBearer) {
			//To trigger the function that had given 401 because the token had expired
			setTriggerAfterUpdatingBearer(false);
			if (logToken !== "") checkLoggedUserMAxQueries();
		}
	}, [logToken]);

	async function clickSendButton(e) {
		if (streaming) {
			stopSearching();
		} else {
			await sendMessage();
		}
	}

	function stopSearching() {
		resetRequestController();
		dispatch(resetVariantValidator());

		if (variantValidatorShow) {
			const pendingMessageIndex = messages.length - 1;
			const pendingMessage = messages[pendingMessageIndex];
			dispatch(
				updateMessage({
					i: pendingMessageIndex,
					obj: {
						text: pendingMessage?.pending?.clearTextOnInterrupt
							? ""
							: (pendingMessage?.text ?? ""),
						stopText:
							pendingMessage?.pending?.interruptionText ??
							"Variant selection has been interrupted by the user.",
						pending: null,
					},
				}),
			);
			dispatch(setShowLoader(false));
			setStopSearch(true);
			return;
		}

		if (messages[messages.length - 1].type === "topic") {
			dispatch(
				updateMessage({
					i: messages.length - 2,
					obj: {
						stopText: "\nVariant search has been interrupted by the user.",
					},
				}),
			);
			dispatch(deleteMessage());
		} else {
			if (loader) {
				dispatch(
					updateMessage({
						i: messages.length - 1,
						obj: {
							stopText: "Variant search has been interrupted by the user.",
							text: "",
						},
					}),
				);
				dispatch(setShowLoader(false));
			} else {
				dispatch(
					updateMessage({
						i: messages.length - 1,
						obj: {
							stopText: "\nVariant search has been interrupted by the user.",
						},
					}),
				);
			}
		}
		setStopSearch(true);
	}

	function getCountry() {
		try {
			return citiesCountryMapping[
				Intl.DateTimeFormat().resolvedOptions().timeZone.split("/")[1]
			];
		} catch (e) {
			return "";
		}
	}

	function getExternalLinkSource() {
		try {
			console.log(
				"getExternalLinkSource",
				window.location.search.split("source=")[1].split("&")[0],
			);
			return window.location.search.split("source=")[1].split("&")[0];
		} catch (e) {
			console.log(e);
			return "";
		}
	}

	function checkIfCreateWantToKnowMoreMessageForUnlogged(statusCode) {
		if (!logToken) {
			if (statusCode === 0) {
				dispatch(
					setMessage({
						text: '{"chat":[{"topic":"How is this linked to diseases?"},{"topic":"What treatments are associated?"},{"topic":"What is the impact of this?"}]}',
						user: "VarChat",
						type: "topic",
						isExampleTopic: true,
						language: "EN",
						statusCode: 0,
						statusInfoCode: 0,
					}),
				);
			}
		}
	}

	async function actuallySendMessage() {
		checkCacheStatus();
		sendMessageGA();
		const validation = validateVariant(inputMessage);
		const text = validation?.text;
		dispatch(
			setMessage({ text: inputMessage, user: "You", linebreak: linebreak }),
		);
		dispatch(setInputMessage(""));
		dispatch(setStreaming(true));
		dispatch(setLineBreak(false));
		if (validation.val) {
			const filteredParams = [
				text.rs,
				text.hgvs_c,
				text.hgvs_p,
				text.genomic_coord,
				text.hgvs_m,
			].filter((str) => str !== "");
			let loading_message = "";
			if (filteredParams.length !== 0) {
				loading_message = "Validating variant " + inputMessage.trim();
			} else {
				loading_message = "Validating gene " + text.gene;
			}
			dispatch(setMessage({ text: loading_message, user: "VarChat" }));
			try {
				const param = Object.assign({}, text, {
					stream: true,
					user: user,
					user_input: inputMessage.trim(),
					country: getCountry(),
					source: urlSource,
				});
				await variantValidator(param);
			} catch (error) {
				console.error(`stream: ${error}`);
			}
		} else {
			dispatch(
				setMessage({ text: "Searching " + inputMessage, user: "VarChat" }),
			);
			try {
				const param = {
					gene: "",
					rs: "",
					hgvs_c: "",
					hgvs_p: "",
					hgvs_m: "",
					transcript: "",
					genomic_coord: "",
					stream: true,
					user: user,
					user_input: inputMessage.trim(),
					country: getCountry(),
					source: urlSource,
				};
				await sendVariantValidateFalse(param, text);
				dispatch(setRefreshProfileHistory(true));
			} catch (error) {
				console.error(`send: ${error}`);
			}

			setTimeout(() => {
				dispatch(deleteMessage());
				dispatch(setStreaming(false));
				dispatch(
					setMessage({
						text: text,
						user: "VarChat",
						statusCode: validation.status,
					}),
				);
			}, 2000);
		}
	}

	async function checkLoggedUserMAxQueries() {
		if (logToken) {
			dispatch(setWaitingForMaxQueryResponse(true));
			dispatch(setIsTopicClickable(false)); // disabilito l'evetuale topic gia esistente mentre qua in streaming

			const loggedMaxQueriesPromise = fetch(
				Url + "/varchat/api/user-info/query-limit-check",
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${logToken}`,
					},
				},
			);
			loggedMaxQueriesPromise
				.then((response) => {
					if (!response.ok) {
						if (response.status === 401) {
							dispatch(setExpired(true));
							setTriggerAfterUpdatingBearer(true);
						}
						if (response.status === 409) {
							dispatch(setShowMaximumQueryLimit(true));
						}
						dispatch(setWaitingForMaxQueryResponse(false));
						throw new Error(`Limit query 409 error: ${response.status}`);
					} else {
						dispatch(setWaitingForMaxQueryResponse(false));
						actuallySendMessage();
					}
				})
				.catch((error, x) => {
					dispatch(setWaitingForMaxQueryResponse(false));
					dispatch(setIsTopicClickable(true));
					dispatch(setStreaming(false));
				});
		}
	}

	async function sendMessage() {
		if (inputMessage.trim(" ") !== "") {
			/* dispatch(setStreaming(true));
			dispatch(setLineBreak(false)); */
			if (logToken === "") {
				const now = Date.now();
				const hasActiveUnloggedLimit =
					unloggedUserCounterInitTime !== 0 &&
					now - unloggedUserCounterInitTime <=
					NOT_LOGGED_USER_COUNTER_RESET_TIME &&
					unloggedUserQueryDone >= NOT_LOGGED_USER_MAX_ALLOWED_QUERIES;

				if (hasActiveUnloggedLimit) {
					dispatch(
						setShowLogin({ show: true, type: "unloggedUserQueryLimit" }),
					);
					return;
				}

				dispatch(setAllTopicMessagesAsObsolete()); // per il discover more degli unlogged

				dispatch(setRequest(nRequest + 1));
				dispatch(setCounterPlusOne(now));
				await actuallySendMessage();
				dispatch(setActiveConversationUuid(nRequest + "unlogged_fake_topic")); // per il discover more degli unlogged
			} else {
				dispatch(setActiveConversationUuid(null));
				await checkLoggedUserMAxQueries();
			}
		}
	}

	//TODO da capire cosa serve questa false
	async function sendVariantValidateFalse(param, text) {
		const requestController = getRequestController();
		let headers;
		logToken
			? (headers = {
				"Content-Type": "application/json",
				Authorization: `Bearer ${logToken}`,
			})
			: (headers = { "Content-Type": "application/json" });

		await fetchEventSource(
			Url + "/varchat/api/request/?" + new URLSearchParams(param),
			{
				method: "GET",
				headers: headers,
				openWhenHidden: true,
				signal: requestController.signal,
				onopen(response) {
					if (requestController.signal.aborted) return;
					if (!response.ok) {
						throw new Error(`HTTP error: ${response.status}`);
					} else {
						return;
					}
				},
				onclose() {
					if (requestController.signal.aborted) return;
					dispatch(setIsTopicClickable(true));
				},
				onerror(error) {
					if (requestController.signal.aborted) return;
					dispatch(setIsTopicClickable(true));
					throw error;
				},
			},
		);
	}

	async function variantValidator(param) {
		const requestController = getRequestController();
		const headers = {
			"Content-Type": "application/json",
			Accept: "application/json",
			...(logToken && { Authorization: `Bearer ${logToken}` }),
		};

		let variants;
		let selectionContext;
		let feedback;
		let responseFeedback = "";
		let invFeedback = "";
		let responseStatusCode;
		//variant-validator, retrieves variant_list and feedback
		try {
			const response = await fetch(
				Url +
				"/varchat/api/request/?" +
				new URLSearchParams({ ...param, stream: true }),
				{
					method: "GET",
					headers,
					signal: requestController.signal,
				},
			);

			if (!response.ok) {
				throw new Error(`HTTP error: ${response.status}`);
			}

			const data = await response.json();
			responseStatusCode = data.status_answer_code ?? -1;
			responseFeedback = Array.isArray(data.feedback) ? data.feedback[0] : "";
			const receivedFeedback = Array.isArray(data.feedback) ? data.feedback[0] : data.feedback;
			invFeedback = responseStatusCode === 12 && typeof receivedFeedback === "string" && receivedFeedback.includes("Currently INV type variants are not supported.") ? receivedFeedback.trim() : "";
			if (!Array.isArray(data.variant_list)) {
				throw new Error(
					responseFeedback ||
					"Invalid response format: variant_list is not an array.",
				);
			}

			variants = data.variant_list.map((variant, selectedIndex) => ({
				...variant,
				selectedIndex,
			}));

			if (variants.length === 0) {
				dispatch(setStreaming(false));
				dispatch(setShowLoader(false));
				dispatch(deleteMessage());
				dispatch(
					setMessage({
						user: "VarChat",
						text: responseStatusCode === undefined ? responseFeedback || "No matching variants were found." : undefined,
						...(responseStatusCode !== undefined && {
							statusCode: responseStatusCode,
						}),
						...(invFeedback && { feedback: invFeedback }),
					}),
				);
				dispatch(setIsTopicClickable(true));
				return;
			}

			if (
				typeof data.uuid !== "string" ||
				data.uuid.trim() === "" ||
				typeof data.context_token !== "string" ||
				data.context_token.trim() === ""
			) {
				throw new Error(
					responseFeedback ||
					"Invalid response format: uuid or context_token is missing.",
				);
			}

			selectionContext = {
				uuid: data.uuid,
				contextToken: data.context_token,
			};
			feedback = responseFeedback;
			//extra check on input, for safety
			if (
				variants.some(
					(variant) =>
						typeof variant?.input !== "string" ||
						variant.input.trim() === "" ||
						typeof variant?.selectedIndex !== "number",
				)
			) {
				throw new Error(responseFeedback || "No valid variants were returned.");
			}
		} catch (error) {
			if (
				requestController.signal.aborted ||
				(error instanceof DOMException && error.name === "AbortError")
			) {
				return;
			}
			console.log("error", error);
			const errorMessage = String(error);
			const isUnauthorized = errorMessage.includes("401");
			dispatch(setStreaming(false));
			dispatch(setShowLoader(false));
			dispatch(deleteMessage());
			dispatch(
				setMessage({
					user: "VarChat",
					statusCode:
						isUnauthorized ? 100 : 5,
				}),
			);
			dispatch(setIsTopicClickable(true));
			return;
		}

		//if found only one it means it's the only variant for the search, directly trigger the streamsummary
		if (variants.length === 1) {
			const selectedText = createSelectedVariant(variants[0]);
			const newMessageIndex = messages.length + 1;
			const summaryWarning =
				responseStatusCode === 0 ? responseFeedback : "";
			dispatch(deleteMessage());
			dispatch(
				setMessage({
					text: createSelectedVariantSearchMessage(variants[0]),
					user: "VarChat",
				}),
			);
			await streamSummary(
				selectedText,
				newMessageIndex,
				variants[0],
				selectionContext,
				summaryWarning,
			);
			return;
		}

		dispatch(deleteMessage());
		dispatch(setShowLoader(false));
		if (
			responseFeedback ||
			(responseStatusCode !== undefined && responseStatusCode !== 0)
		) {
			dispatch(
				setMessage({
					text: invFeedback ? "" : responseFeedback,
					user: "VarChat",
					statusCode: responseStatusCode,
					...(invFeedback && { feedback: invFeedback }),
				}),
			);
		}
		dispatch(
			setMessage({
				text: "Waiting for the variant selection from the user...",
				user: "VarChat",
				pending: {
					interruptionText:
						"Variant selection has been interrupted by the user.",
					clearTextOnInterrupt: true,
				},
			}),
		);
		dispatch(
			openVariantValidator({
				variants,
				selectionContext,
				feedback,
				statusCode: responseStatusCode,
			}),
		);
	}

	async function streamSummary(
		text,
		newMessageIndex,
		variant,
		selectionContext,
		warningText = "",
	) {
		const requestController = getRequestController();
		let statusCode;
		let conversationUuid;
		let jsonData = {};
		const headers = {
			"Content-Type": "application/json",
			Accept: "text/event-stream",
			...(logToken && { Authorization: `Bearer ${logToken}` }),
		};
		dispatch(setShowLoader(true));
		await fetchEventSource(Url + "/varchat/api/request/select-variant", {
			method: "POST",
			headers: headers,
			body: JSON.stringify({
				uuid: selectionContext.uuid,
				context_token: selectionContext.contextToken,
				selected_index: variant.selectedIndex,
			}),
			openWhenHidden: true,
			signal: requestController.signal,
			onopen(response) {
				if (requestController.signal.aborted) return;
				dispatch(setRefreshProfileHistory(true));
				if (!response.ok) {
					if (response.status === 401) {
						setTimeout(function () {
							// fix the already active validation when coming from unlogged user max query limit
							dispatch(
								setShowLogin({ show: true, type: "unloggedUserQueryLimit" }),
							);
						}, 100);
					}
					throw new Error(`HTTP error: ${response.status}`);
				}

				const contentType = response.headers.get("content-type");
				if (!contentType?.includes("text/event-stream")) {
					throw new Error(`Unexpected content type: ${contentType}`);
				}
			},
			onmessage(msg) {
				if (requestController.signal.aborted) return;
				try {
					const data = JSON.parse(msg.data);
					//console.log('onmessage data', data);
					if (Object.keys(jsonData).length === 0) {
						statusCode = data.status_answer_code;
						const feedback = Array.isArray(data.feedback) ? data.feedback[0] : data.feedback;
						const invFeedback = data.status_answer_code === 12 && typeof feedback === "string" && feedback.includes("Currently INV type variants are not supported.") ? feedback.trim() : "";
						dispatch(deleteMessage());
						dispatch(setShowLoader(false));
						dispatch(
							setMessage({
								text: "",
								user: "VarChat",
								userInput: text,
								statusCode: data.status_answer_code,
								language: "EN",
								statusInfoCode: data.status_info_code,
								...(invFeedback && { feedback: invFeedback }),
								...(warningText && { warningText }),
							}),
						);
						jsonData = {
							evaluate: true,
							cits: data.cits,
							citCount: data.cit_count,
							rcvCitCount: data.rcv_cit_count,
							rcvCits: data.rcv_cits,
							triggeringQuestionLambda: data.triggering_question_lambda,
							conversationUuid: logToken
								? data.conversation_uuid
								: nRequest + "unlogged_fake_topic", // per il discover more degli unlogged
						};
						if (data.triggering_question_lambda) {
							dispatch(
								setMessage({
									//text: '{"chat":[{"topic":"Parkinsons disease and PINK1 mutation","topic_uuid":"b6f02b53-1948-45af-b47a-c46885a4f10d","questions":[{"question":"What is the role of the PINK1 G309D mutation in the development of Parkinsons disease?","question_uuid":"e920a82b-3046-4867-843c-52139538cbb1"},{"question":"How does the PINK1 G309D mutation affect mitochondrial function and oxidative stress in Parkinsons disease?","question_uuid":"14f61b6c-5232-4932-83a9-5c4cf5ac8e96"}]},{"topic":" Treatment implications of PINK1 mutation","topic_uuid":"46535f00-7048-4fc6-8411-4f5ebc0feba3","questions":[{"question":"How does the PINK1 G309D mutation influence the effectiveness of proteasome inhibitors in treating Parkinsons disease?","question_uuid":"dbf46a6f-5164-4d65-a2fc-7b61702df0f9"}]},{"topic":" PINK1 mutation and kinase activity","topic_uuid":"c60d5608-385c-4132-8292-ef10a4bb2a5d","questions":[{"question":"What impact does the PINK1 G309D mutation have on the kinase activity of the PINK1 protein?","question_uuid":"cca5aa6b-7e5a-4ec8-9d8b-110a60ac828d"}]},{"topic":" Tau phosphorylation and neurodegeneration","topic_uuid":"2d700926-ae21-4ce4-8be3-871ed3c6e0c5","questions":[{"question":"How does the PINK1 G309D mutation affect tau phosphorylation and its implications for neurodegenerative diseases?","question_uuid":"7b8b9e8f-1056-4e78-a7c6-2c1dd77b7d87"}]}],"input_tokens":3318,"status_answer_code":0,"output_tokens":159,"date_time":"2024-10-10","model":"gpt-4o","conversation_uuid":"a63de090-eacc-4bb3-97c0-267ec960a587","time":3.260653018951416}',
									text: '{"chat":[]}',
									user: "VarChat",
									type: "topic",
									language: "EN",
									conversationUuid: data.conversation_uuid,
									statusCode: data.status_answer_code,
									statusInfoCode: data.status_answer_code,
									cits: data.cits,
									citCount: data.cit_count,
								}),
							);
							conversationUuid = data.conversation_uuid;
						}
					} else {
						if (data.status_answer_code !== undefined) {
							jsonData = Object.assign(jsonData, {
								statusCode: data.status_answer_code,
							});
							// console.log('dati', jsonData, 'dataa', data);
						} else {
							dispatch(
								updateMessageText({ i: newMessageIndex, text: data }),
							);
							// console.log('status answer undefined', data)
						}
					}
				} catch {
					const word = msg.data === "" ? "\n" : msg.data;
					// console.log('data word', msg.data, 'word', word)
					dispatch(updateMessageText({ i: newMessageIndex, text: word }));
				}
			},
			onclose() {
				if (requestController.signal.aborted) return;
				dispatch(updateMessage({ i: newMessageIndex, obj: jsonData }));
				dispatch(setStreaming(false)); //moved to use effect of messages (for scrolling purposes) // rimesso anche qui
				if (conversationUuid) {
					dispatch(setActiveConversationUuid(conversationUuid));
				}
				dispatch(setIsTopicClickable(true));
				checkIfCreateWantToKnowMoreMessageForUnlogged(statusCode);
			},
			onerror(error) {
				if (requestController.signal.aborted) return;
				console.log("error", error);
				error += "";
				dispatch(setStreaming(false));
				dispatch(setShowLoader(false));
				dispatch(deleteMessage());
				dispatch(
					setMessage({
						user: "VarChat",
						statusCode: error.includes("401") ? 100 : 5,
					}),
				);
				dispatch(setIsTopicClickable(true));
				throw error;
			},
		});
	}

	const selectedVariant = useSelector(selectSelectedVariant);

	//terribile con useEffect, ma l'app si basa troppo du redux, non si riesce a refactorare e mandarseli come prop
	//on select variant, trigger the stream summary
	useEffect(() => {
		if (!selectedVariant || !variantSelectionContext) {
			return;
		}
		const hasValidatorNotice =
			variantValidatorFeedback !== "" ||
			(variantValidatorStatusCode !== undefined &&
				variantValidatorStatusCode !== 0);
		const newMessageIndex =
			messages.length - (hasValidatorNotice ? 0 : 1);
		dispatch(deleteMessage());
		if (!hasValidatorNotice) {
			dispatch(deleteMessage());
		}
		dispatch(
			setMessage({
				text: createSelectedVariantLabel(selectedVariant),
				user: "You",
			}),
		);
		dispatch(setInputMessage(""));
		dispatch(setLineBreak(false));
		const text = createSelectedVariant(selectedVariant);

		const loading_message = createSelectedVariantSearchMessage(selectedVariant);

		dispatch(setMessage({ text: loading_message, user: "VarChat" }));

		streamSummary(
			text,
			newMessageIndex,
			selectedVariant,
			variantSelectionContext,
		).catch((e) => {
			console.log("error", e);
		});
		dispatch(resetVariantValidator());
	}, [
		selectedVariant,
		variantSelectionContext,
		variantValidatorFeedback,
		variantValidatorStatusCode,
	]);

	return (
		<div name="send-button-div">
			<span>{!streaming ? "SEND" : "STOP"}</span>
			<Button
				name="send-button"
				onClick={clickSendButton}
				ref={buttonRef}
				disabled={waitingForMaxQueryResponse ? true : disable}
			>
				{streaming ? (
					<>
						<StopIcon />
					</>
				) : (
					<>
						<SendArrow />
					</>
				)}
			</Button>
		</div>
	);
}

export default SendMessageButton;